import * as React from 'react';
import { DefaultButton, Dialog, DialogFooter, DialogType, Text, IconButton, List, PrimaryButton, Separator, Stack, TextField, ITextField, Spinner, SpinnerSize, Modal, Label, Button, on } from '@fluentui/react';

import { AppStateContext } from '../../state/AppProvider';
import { GroupedChatHistory } from './ChatHistoryList';

import styles from "./ChatHistoryPanel.module.css"
import { useBoolean } from '@fluentui/react-hooks';
import { Conversation } from '../../api/models';
import { historyDelete, historyRename, historyList, historyChangeFavorite, historyShare } from '../../api';
import { useEffect, useRef, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { LongProcessButton } from '../common/Button';
import { useIsMobile } from '../../hooks/useIsMobile';

interface ChatHistoryListItemCellProps {
  item?: Conversation;
  onSelect: (item: Conversation | null) => void;
}

interface ChatHistoryListItemGroupsProps {
  groupedChatHistory: GroupedChatHistory[];
}

const formatMonth = (month: string) => {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    
    const [monthName, yearString] = month.split(' ');
    const year = parseInt(yearString);

    if (year === currentYear) {
        return monthName;
    } else {
        return month;
    }
};

export const ChatHistoryListItemCell: React.FC<ChatHistoryListItemCellProps> = ({
  item,
  onSelect,
}) => {
    const [isHovered, setIsHovered] = React.useState(false);
    const [isShared, setIsShared] = React.useState(item?.is_shared ?? false);
    const [edit, setEdit] = useState(false);
    const [editTitle, setEditTitle] = useState("");
    const [hideDeleteDialog, { toggle: toggleDeleteDialog }] = useBoolean(true);
    const [errorDelete, setErrorDelete] = useState(false);
    const [renameLoading, setRenameLoading] = useState(false);
    const [errorRename, setErrorRename] = useState<string | undefined>(undefined);
    const [errorChangeFavorite, setErrorChangeFavorite] = useState<string | undefined>(undefined);
    const [textFieldFocused, setTextFieldFocused] = useState(false);
    const textFieldRef = useRef<ITextField | null>(null);
    const navigate = useNavigate()
    const isMobile = useIsMobile();
    const appStateContext = React.useContext(AppStateContext)
    const isSelected = item?.id === appStateContext?.state.currentChat?.id;
    const isGenerating = Array.from(appStateContext?.state.generatingConversations.values() ?? []).some((v) => v.history_metadata.conversation_id === item?.id);
    const dialogContentProps = {
        type: DialogType.close,
        title: '本当にこの履歴を削除しますか？',
        closeButtonAriaLabel: 'Close',
        subText: '削除された履歴は復元できません。',
    };

    const modalProps = {
        titleAriaId: 'labelId',
        subtitleAriaId: 'subTextId',
        isBlocking: true,
        styles: { main: { maxWidth: 450 } },
    }

    if (!item) {
        return null;
    }

    useEffect(() => {
        if (textFieldFocused && textFieldRef.current) {
            textFieldRef.current.focus();
            setTextFieldFocused(false);
        }
    }, [textFieldFocused]);

    useEffect(() => {
        if (appStateContext?.state.currentChat?.id !== item?.id) {
            setEdit(false);
            setEditTitle('')
        }
    }, [appStateContext?.state.currentChat?.id, item?.id]);

    const onDelete = async () => {
        let response = await historyDelete(item.id)
        if(!response.ok){
            setErrorDelete(true)
            setTimeout(() => {
                setErrorDelete(false);
            }, 5000);
        }else{
            appStateContext?.dispatch({ type: 'DELETE_CHAT_ENTRY', payload: item.id })
            navigate("/")
        }
        toggleDeleteDialog();
    };

    const onEdit = () => {
        setEdit(true)
        setTextFieldFocused(true)
        setEditTitle(item?.title)
    };

    const onShare = async () => {
        const success = await historyShare(item.id, true)
        if (success) {
            setIsShared(true)
            const url = `${window.location.origin}/chat/${item.id}`
            await navigator.clipboard.writeText(url);
        }
    }

    const handleSelectItem = async () => {
        onSelect(item)
        appStateContext?.dispatch({ type: "SET_PROMPT_TEMPLATE_OVERRIDE", payload: null })
        // モバイル版はチャット履歴パネルを非表示にする
        if (isMobile) {
            appStateContext?.dispatch({ type: 'SET_SIDEBAR_OPEN', payload: false });
        }
        navigate(`/chat/${item.id}`)
    }

    const truncatedTitle = (item?.title?.length > 28) ? `${item.title.substring(0, 28)} ...` : item.title;

    const handleSaveEdit = async (e: any) => {
        e.preventDefault();
        e.stopPropagation();
        if(errorRename || renameLoading){
            return;
        }
        if(editTitle == item.title){
            setErrorRename("エラー： 新しいタイトルを入力してください。")
            setTimeout(() => {
                setErrorRename(undefined);
                setTextFieldFocused(true);
                if (textFieldRef.current) {
                    textFieldRef.current.focus();
                }
            }, 5000);
            return
        }
        setRenameLoading(true)
        let response = await historyRename(item.id, editTitle);
        if(!response.ok){
            setErrorRename("エラー：チャットのタイトルを変更できませんでした。")
            setTimeout(() => {
                setRenameLoading(false)
                setTextFieldFocused(true);
                setErrorRename(undefined);
                if (textFieldRef.current) {
                    textFieldRef.current.focus();
                }
            }, 5000);
            return
        }else{
            setRenameLoading(false)
            setEdit(false)
            appStateContext?.dispatch({ type: 'UPDATE_CHAT_TITLE', payload: { ...item, title: editTitle } as Conversation })
            setEditTitle("");
        }
    }

    const chatHistoryTitleOnChange = (e: any) => {
        setEditTitle(e.target.value);
    };

    const cancelEditTitle = (e?: any) => {
        e?.preventDefault();
        e?.stopPropagation();
        setEdit(false)
        setEditTitle("");
    }

    const handleKeyPressEdit = (e: any) => {
        if(e.key === "Enter"){
            return handleSaveEdit(e)
        }
        if(e.key === "Escape"){
            cancelEditTitle();
            return
        }
    }

    // モバイル版対応時に追加
    const handleDeleteClick = (e: any) => {
        e.preventDefault();
        e.stopPropagation();
        toggleDeleteDialog();
    }

    const handleEditClick = (e: any) => {
        e.preventDefault();
        e.stopPropagation();
        onEdit();
    }

        // テキストフィールドのイベント伝播を防ぐハンドラー
    const handleTextFieldClick = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleTextFieldMouseDown = (e: React.MouseEvent) => {
        e.stopPropagation();
    };

    const handleTextFieldContextMenu = (e: React.MouseEvent) => {
        e.stopPropagation();
    };

    const handleTextFieldTouchStart = (e: React.TouchEvent) => {
        e.stopPropagation();
    };

    const changeFavorite = async (e: any) => {
        e.preventDefault();
        e.stopPropagation();
        let response = await historyChangeFavorite(item.id, !item.is_favorite);
        if(!response.ok){
            setErrorChangeFavorite("エラー：お気に入り登録できませんでした。")
            setTimeout(() => {
                setErrorChangeFavorite(undefined);
            }, 5000);
            return
        }else{
            appStateContext?.dispatch({ type: 'CHANGE_CHAT_FAVORITE', payload: { ...item, is_favorite: !item.is_favorite } as Conversation })
        }
    } 

    return (
        <Stack
            key={item.id}
            tabIndex={0}
            aria-label='chat history item'
            data-testid={`history-item-${item.id}`}
            data-selected={isSelected}
            data-generating={isGenerating}
            data-favorite={Boolean(item.is_favorite)}
            className={styles.itemCell}
            onClick={() => handleSelectItem()}
            onKeyDown={e => e.key === "Enter" || e.key === " " ? handleSelectItem() : null}
            verticalAlign='center'
            // horizontal
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            styles={{
                root: {
                    backgroundColor: isSelected ? '#e6e6e6' : 'transparent',
                }
            }}
        >
            {edit ? <>
                <Stack.Item 
                    style={{ width: '100%' }}
                >
                    <form aria-label='edit title form' onSubmit={(e) => handleSaveEdit(e)} style={{padding: '5px 0px'}}>
                        <Stack horizontal verticalAlign={'start'}>
                            <Stack.Item>
                                <TextField
                                    componentRef={textFieldRef}
                                    autoFocus={textFieldFocused}
                                    value={editTitle}
                                    placeholder={item.title}
                                    onChange={chatHistoryTitleOnChange}
                                    onKeyDown={handleKeyPressEdit}
                                    // errorMessage={errorRename}
                                    disabled={errorRename ? true : false}
                                    onClick={handleTextFieldClick}
                                    onMouseDown={handleTextFieldMouseDown}
                                    onContextMenu={handleTextFieldContextMenu}
                                    onTouchStart={handleTextFieldTouchStart}
                                    data-testid="history-rename-input"
                                />
                            </Stack.Item>
                            {editTitle && (<Stack.Item>
                                <Stack aria-label='action button group' horizontal verticalAlign={'center'}>
                                    <IconButton role='button' disabled={errorRename !== undefined} onKeyDown={e => e.key === " " || e.key === 'Enter' ? handleSaveEdit(e) : null} onClick={(e) => handleSaveEdit(e)} aria-label='confirm new title' iconProps={{iconName: 'CheckMark'}} styles={{ root: { color: 'green', marginLeft: '5px' } }} />
                                    <IconButton role='button' disabled={errorRename !== undefined} onKeyDown={e => e.key === " " || e.key === 'Enter' ? cancelEditTitle() : null} onClick={(e) => cancelEditTitle(e)} aria-label='cancel edit title' iconProps={{iconName: 'Cancel'}} styles={{ root: { color: 'red', marginLeft: '5px' } }} />
                                </Stack>
                            </Stack.Item>)}
                        </Stack>
                        {errorRename && (
                            <Text role='alert' aria-label={errorRename} style={{fontSize: 12, fontWeight: 400, color: 'rgb(164,38,44)'}}>{errorRename}</Text>
                        )}
                        {errorChangeFavorite && (
                            <Text role='alert' aria-label={errorChangeFavorite} style={{fontSize: 12, fontWeight: 400, color: 'rgb(164,38,44)'}}>{errorChangeFavorite}</Text>
                        )}
                    </form>
                </Stack.Item>
            </> : <>
                <Stack horizontal verticalAlign={'center'} style={{ width: '100%' }}>
                    {
                        isGenerating && <Spinner style={{marginRight: "6px"}} />
                    }
                    <div className={styles.chatTitle}>{truncatedTitle}</div>
                    {!isMobile && (isSelected || isHovered) && <Stack horizontal horizontalAlign='end'>
                        <LongProcessButton className={styles.itemButton} title={"共有URLをクリップボードにコピー"} onClick={onShare} icon={isShared ? 'BoxCheckmarkSolid' : 'Share'} testId="history-share-button"/>
                        <IconButton className={styles.itemButton} iconProps={{ iconName: 'Delete' }} title="削除" data-testid="history-delete-button" onClick={handleDeleteClick} onKeyDown={e => e.key === " " ? toggleDeleteDialog() : null}/>
                        <IconButton className={styles.itemButton} iconProps={{ iconName: 'Edit' }} title="編集" data-testid="history-edit-button" onClick={handleEditClick} onKeyDown={e => e.key === " " ? onEdit() : null}/>
                        {!item.is_favorite && 
                            <IconButton className={styles.itemButton} iconProps={{ iconName: 'AddFavorite' }} title="お気に入り" data-testid="history-favorite-button" onClick={e => changeFavorite(e)} onKeyDown={e => e.key === " " ? changeFavorite(e) : null}/>
                        }
                    </Stack>}
                    {item.is_favorite &&
                        <div style={{marginLeft:"auto"}}>
                            <IconButton className={styles.itemButton} iconProps={{ iconName: 'FavoriteStarFill' }} title="お気に入り" data-testid="history-favorite-button" onClick={e => changeFavorite(e)} onKeyDown={e => e.key === " " ? changeFavorite(e) : null}/>
                        </div>
                    }
                </Stack>
            </>
            }
            {errorDelete && (
                <Text
                    styles={{
                        root: { color: 'red', marginTop: 5, fontSize: 14 }
                    }}
                >
                    Error: could not delete item
                </Text>
            )}
            <Dialog
                hidden={hideDeleteDialog}
                onDismiss={toggleDeleteDialog}
                dialogContentProps={dialogContentProps}
                modalProps={modalProps}
            >
                <DialogFooter>
                <PrimaryButton onClick={onDelete} text="削除" />
                <DefaultButton onClick={toggleDeleteDialog} text="キャンセル" />
                </DialogFooter>
            </Dialog>
        </Stack>
    );
};

// appStateContext?.state.chatHistoryは今までに読み込んだすべてのチャットが格納されている
// groupedChatHistoryは、現在のRAGIDに従ってフィルタリングされたものが格納されている
export const ChatHistoryListItemGroups: React.FC<ChatHistoryListItemGroupsProps> = ({ groupedChatHistory }) => {
    const [ , setSelectedItem] = React.useState<Conversation | null>(null);

    const handleSelectHistory = (item?: Conversation) => {
      if(item){
          setSelectedItem(item)
      }
    }

    const onRenderCell = (item?: Conversation) => {
      return (
        <ChatHistoryListItemCell item={item} onSelect={() => handleSelectHistory(item)} />
      );
    };

  return (
    <div data-is-scrollable style={{maxWidth: "100%", width: "100%"}}>
      {groupedChatHistory.map((group) => (
        group.entries.length > 0 && <Stack horizontalAlign="start" verticalAlign="center" key={group.month} className={styles.chatGroup} aria-label={`chat history group: ${group.month}`}>
          <Stack aria-label={group.month} className={styles.chatMonth}>{formatMonth(group.month)}</Stack>
          <List aria-label={`chat history list`} items={group.entries} onRenderCell={onRenderCell} className={styles.chatList}/>
          <Separator styles={{
            root: {
                width: '100%',
                position: 'relative',
                '::before': {
                  backgroundColor: '#d6d6d6',
                },
              },
          }}/>
        </Stack>
      ))}
    </div>
  );
};
